import {
  type AltoIconName,
  BORDERS,
  COLORS,
  type ColorVariant,
  type TEXT_COLORS_VALUES,
} from '@alto/design-library-tokens';
import { AltoIcon, Description, Row, SmPadding, XsSpacing } from '@alto/design-system';
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  offset,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

type ColorProps = {
  success?: boolean | null | undefined;
  warning?: boolean | null | undefined;
  danger?: boolean | null | undefined;
  link?: boolean | null | undefined;
  light?: boolean | null | undefined;
};

const Tooltip = styled.div`
  background-color: ${COLORS.BACKGROUND_COLORS.PRIMARY};
  border-radius: ${BORDERS.RADIUS.SM.px};
  color: ${COLORS.TEXT_COLORS.WHITE};
  width: max-content;
  max-width: min(400px, 50vw);
  text-align: center;
`;

function getIconColor({ success, warning, danger, link, light }: ColorProps): ColorVariant {
  if (success) {
    return 'success';
  }

  if (warning) {
    return 'danger';
  }

  if (danger) {
    return 'danger';
  }

  if (link) {
    return 'secondary';
  }

  if (light) {
    return 'disabled';
  }

  return 'grey';
}

const getTextColor = ({ success, warning, danger, link, light }: ColorProps): TEXT_COLORS_VALUES => {
  if (success) {
    return COLORS.TEXT_COLORS.SUCCESS;
  }

  if (warning) {
    return COLORS.TEXT_COLORS.WARNING;
  }

  if (danger) {
    return COLORS.TEXT_COLORS.DANGER;
  }

  if (link) {
    return COLORS.TEXT_COLORS.SECONDARY;
  }

  if (light) {
    return COLORS.TEXT_COLORS.DISABLED;
  }

  return COLORS.TEXT_COLORS.GREY;
};

type Props = {
  readonly bold?: boolean;
  readonly children: string;
  readonly danger?: boolean;
  readonly icon: AltoIconName;
  readonly infoText?: string;
  readonly success?: boolean;
  readonly warning?: boolean;
  readonly link?: boolean;
  readonly lightText?: boolean;
};
export const InfoRow: React.FC<Props> = ({
  bold = false,
  danger = false,
  infoText = '',
  link = false,
  success = false,
  warning = false,
  lightText = false,
  children,
  icon,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [
      offset(10),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const role = useRole(context, { role: 'tooltip' });

  const { getFloatingProps, getReferenceProps } = useInteractions([hover, role]);

  if (!children) return null;

  return (
    <Row centerVertically>
      <AltoIcon
        name={icon}
        type={getIconColor({ success, warning, danger, link })}
      />
      <XsSpacing />
      <Description
        fontFamily={bold ? 'bold' : undefined}
        color={getTextColor({ danger, success, warning, link, light: lightText })}
      >
        {children}
      </Description>
      <XsSpacing />
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <AltoIcon
          name="info-small"
          type="secondary"
        />
      </span>
      <FloatingPortal>
        {isOpen && infoText && (
          <Tooltip
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={COLORS.BACKGROUND_COLORS.PRIMARY}
            />
            <SmPadding>{infoText}</SmPadding>
          </Tooltip>
        )}
      </FloatingPortal>
    </Row>
  );
};
